import {t, Trans} from '@lingui/macro';
import {SideObject} from '@popperjs/core';
import {compact} from 'lodash';
import memoize from 'lru-memoize';
import React, {ReactNode} from 'react';

import {KeyboardActionsEnum} from '../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {CompanyFeaturesEnum} from '../../../models/api/companyModel';
import {PermissionsEnum} from '../../../models/api/roleModel';
import {AppContentTypesEnum} from '../../../store/panels/app/parameters/appParameters';
import {PanelTypesEnum} from '../../../store/panels/panelStateBase';
import {Localizer} from '../../../store/stateless/internationalizationSelectors';
import {AppRoutesPrefixesEnum} from '../../appRoutesPrefixes';

/*
 * Types.
 */

export interface AppDefinition {
  iconName: IconName;
  title: ReactNode;
  localizedTitle?: string;
  contentType: AppContentTypesEnum;
  /** @deprecated Use the `contentType` property instead. */
  path: string;
  /** @deprecated Use the `contentType` property instead. */
  contentPanelType?: PanelTypesEnum | string; // @TODO: Remove string when CONTACTS is in PanelTypesEnum.
  permission?: PermissionsEnum;
  feature?: CompanyFeaturesEnum;
  keyboardAction?: KeyboardActionsEnum;
}

export enum TrackingLocationsEnum {
  P0 = 'p0',
}

/*
 * Constants.
 */

export const topBarHeight = 52;
export const topBarHorizontalPadding = 16;
export const topBarVerticalPadding = 10;

export const minTopBarSearchBarWidth = 314;
export const maxTopBarSearchBarWidth = 530;

export const settingsButtonMargin = 12;

export const topBarTooltipCustomMargin = 4;
export const topBarTooltipPreventOverflowPadding: Partial<SideObject> = {
  // Align the left/right edge of the tooltip with the left/right edge of the anchor.
  left: topBarHorizontalPadding - topBarTooltipCustomMargin,
  right: topBarHorizontalPadding - topBarTooltipCustomMargin,
};

/*
 * App definitions.
 */

interface BuildAppsOptions {
  shouldDisplayAuditTrail: boolean;
  shouldDisplayNewContacts: boolean;
  // Needed for the palette.
  localize?: Localizer;
}

export const buildApps = memoize()((options: BuildAppsOptions) =>
  compact<AppDefinition>([
    {
      iconName: 'inboxFilled',
      title: <Trans id="tab-inbox">Inbox</Trans>,
      localizedTitle: options.localize && options.localize(t('tab-inbox')`Inbox`),
      contentType: AppContentTypesEnum.INBOXES,
      path: `/${AppRoutesPrefixesEnum.INBOXES}`,
      contentPanelType: PanelTypesEnum.INBOXES,
      keyboardAction: KeyboardActionsEnum.SWITCH_TO_INBOX,
    },
    {
      iconName: 'calendar',
      title: <Trans id="tab-calendar">Calendar</Trans>,
      localizedTitle: options.localize && options.localize(t('tab-calendar')`Calendar`),
      contentType: AppContentTypesEnum.CALENDARS,
      path: `/${AppRoutesPrefixesEnum.CALENDAR}`,
      contentPanelType: PanelTypesEnum.CALENDARS,
      keyboardAction: KeyboardActionsEnum.SWITCH_TO_CALENDAR,
    },
    {
      iconName: 'contacts',
      title: <Trans id="tab-contacts">Contacts</Trans>,
      localizedTitle: options.localize && options.localize(t('tab-contacts')`Contacts`),
      contentType: AppContentTypesEnum.CONTACTS,
      path: `/${AppRoutesPrefixesEnum.CONTACTS}`,
      contentPanelType: 'CONTACTS', // @TODO: Replace with PanelTypesEnum.
      keyboardAction: KeyboardActionsEnum.SWITCH_TO_CONTACTS,
    },
    {
      iconName: 'analytics',
      title: <Trans id="tab-analytics">Analytics</Trans>,
      localizedTitle: options.localize && options.localize(t('tab-analytics')`Analytics`),
      contentType: AppContentTypesEnum.ANALYTICS,
      path: `/${AppRoutesPrefixesEnum.ANALYTICS}`,
      contentPanelType: PanelTypesEnum.ANALYTICS,
      permission: PermissionsEnum.READ_ANALYTICS,
      feature: CompanyFeaturesEnum.ANALYTICS,
      keyboardAction: KeyboardActionsEnum.SWITCH_TO_ANALYTICS,
    },
    {
      iconName: 'sequences',
      title: <Trans id="tab-sequences">Sequences</Trans>,
      localizedTitle: options.localize && options.localize(t('tab-sequences')`Sequences`),
      contentType: AppContentTypesEnum.SEQUENCES,
      path: `/${AppRoutesPrefixesEnum.SEQUENCES}`,
      permission: PermissionsEnum.READ_SEQUENCE,
      feature: CompanyFeaturesEnum.SEQUENCES,
    },
    options.shouldDisplayAuditTrail && {
      iconName: 'auditTrail',
      title: <Trans id="tab-audittrail">Audit trail</Trans>,
      localizedTitle: options.localize && options.localize(t('tab-audittrail')`Audit trail`),
      contentType: AppContentTypesEnum.AUDIT_TRAIL,
      path: `/${AppRoutesPrefixesEnum.AUDIT_TRAIL}`,
      permission: PermissionsEnum.READ_AUDIT,
    },
  ]),
);
